/**
 * Resolving a pointer position into a candidate design — pure, no DOM.
 *
 * Every plan-view gesture runs through `resolvePlanDrag`, and the shape of that
 * function is the whole safety argument for having a second editing surface:
 *
 *   pointer -> transform -> reconcile -> validate -> (capacity)
 *
 * The two middle steps are the EXISTING domain functions. `reconcileInteriorLayout`
 * decides what fits inside the walls; `validateInteriorLayout` decides what is
 * legal. This module contributes neither a clearance number nor a bounds check
 * of its own — if it did, the plan view and the 3D view would eventually
 * disagree about the same design, which is the risk the issue calls out.
 *
 * The candidate never touches the store. It is committed once, on release, and
 * only when it is valid — see `usePlanDrag`.
 */
import type { RuleOverrides } from "../../../data/ruleRegistry";
import {
	type BenchRunEdge,
	calculateSeatingCapacity,
	type HeaterSizeMm,
	type InteriorDoor,
	type InteriorLayout,
	type InteriorLayoutIssue,
	type InteriorLayoutValidationResult,
	type InteriorRoom,
	type InteriorWall,
	moveBenchRunAlongWall,
	moveHeaterAlongWall,
	moveHeaterToWall,
	reconcileInteriorLayout,
	resizeBenchRun,
	type SeatingCapacityResult,
	snapMm,
	validateInteriorLayout,
	wallAxisOriginMm,
	wallRunsAlongX,
} from "../../../domain/interior";
import type { Market } from "../../../domain/location/types";
import {
	type OpeningLike,
	validateOpeningSet,
} from "../../../domain/openings/validateOpeningSet";
import type { HeaterType, SaunaDimensions } from "../../../engines/types";

/** What the pointer currently has hold of. */
export type PlanDragTarget =
	| { kind: "bench"; runId: string }
	| { kind: "bench-edge"; runId: string; edge: BenchRunEdge }
	| { kind: "heater" }
	| { kind: "door"; openingId: string };

/**
 * Everything the resolution needs that does not change during a gesture.
 * Derived once, from the engine's own `deriveInteriorLayoutContext`, so the
 * plan view measures the same room the BOM does.
 */
export interface PlanDragContext {
	room: InteriorRoom;
	relaxRoom: InteriorRoom | null;
	heaterSize: HeaterSizeMm;
	heaterType: HeaterType;
	heaterModelId?: string | null;
	dimensions: SaunaDimensions;
	doors: InteriorDoor[];
	relaxDoors: InteriorDoor[];
	ceilingHeightMm?: number;
	overrides?: RuleOverrides;
	market?: Market;
}

export interface PlanDragResolution {
	layout: InteriorLayout;
	openings: OpeningLike[];
	/** False when any blocking issue is present — drives the red zone. */
	valid: boolean;
	/** Issues concerning the element being dragged, for the live explanation. */
	issues: InteriorLayoutIssue[];
	/** Non-domain reason the move is refused (an opening leaving its wall). */
	openingIssueKey: PlanOpeningIssueKey | null;
	capacity: SeatingCapacityResult;
}

export type PlanOpeningIssueKey =
	| "plan_view_issue_openingBounds"
	| "plan_view_issue_openingOverlap";

/** Model-frame point, millimetres. */
export interface PlanPointMm {
	xMm: number;
	zMm: number;
}

/**
 * Project a model-frame point onto the axis a wall runs along, measured from
 * that wall's low-coordinate corner — the same coordinate `BenchRun.startMm`
 * and `HeaterPlacement.offsetMm` are stored in.
 */
export function pointAlongWallMm(
	point: PlanPointMm,
	room: InteriorRoom,
	wall: InteriorWall,
): number {
	const axisValue = wallRunsAlongX(wall) ? point.xMm : point.zMm;
	return axisValue - wallAxisOriginMm(room, wall);
}

/** The room a run belongs to, or the hot room when it names an unknown one. */
export function roomForRun(
	roomId: string,
	context: PlanDragContext,
): InteriorRoom {
	return roomId === "relax" && context.relaxRoom
		? context.relaxRoom
		: context.room;
}

/**
 * One issue, plus the room it was raised in.
 *
 * `InteriorLayoutIssue` carries no room: `validateInteriorLayout` is called
 * once per room and the caller knows which. That is fine for display and fatal
 * for the baseline comparison — a `free-floor` issue has neither a `runId` nor
 * a room, so the hot room's and the relax room's were indistinguishable, and an
 * existing violation in one excused a brand new one in the other.
 */
interface RoomScopedIssue {
	roomId: string;
	issue: InteriorLayoutIssue;
}

interface PlanValidationResult extends InteriorLayoutValidationResult {
	scoped: RoomScopedIssue[];
}

function validateAllRooms(
	layout: InteriorLayout,
	context: PlanDragContext,
): PlanValidationResult {
	const hot = validateInteriorLayout(
		{
			layout,
			room: context.room,
			heaterType: context.heaterType,
			heaterModelId: context.heaterModelId,
			heaterSize: context.heaterSize,
			doors: context.doors,
			ceilingHeightMm: context.ceilingHeightMm,
		},
		context.overrides ?? {},
		context.market ?? "se",
	);
	const scopeTo = (roomId: string, result: InteriorLayoutValidationResult) =>
		result.issues.map((issue) => ({ roomId, issue }));

	if (!context.relaxRoom) {
		return { ...hot, scoped: scopeTo(context.room.id, hot) };
	}

	// The relax side has no heater, so nothing is clipped and no clearance
	// issue can be raised there — `validateInteriorLayout` documents the
	// omitted `heaterSize` as exactly that state.
	const relax = validateInteriorLayout(
		{
			layout,
			room: context.relaxRoom,
			heaterType: context.heaterType,
			doors: context.relaxDoors,
			ceilingHeightMm: context.ceilingHeightMm,
		},
		context.overrides ?? {},
		context.market ?? "se",
	);
	return {
		valid: hot.valid && relax.valid,
		issues: [...hot.issues, ...relax.issues],
		scoped: [
			...scopeTo(context.room.id, hot),
			...scopeTo(context.relaxRoom.id, relax),
		],
	};
}

/**
 * Where a gesture started: the pointer's position along the element's wall,
 * and the element's own stored value at that moment.
 *
 * Both halves matter, and the first version of this module had neither right.
 * Snapping the POINTER position (rather than the movement) meant a bare click
 * rewrote every off-grid value the engine generates — and every generated
 * `heater.offsetMm` is off-grid, because `interiorLayoutResolver` rounds to
 * 0.1 mm. Omitting the origin for the heater made it teleport its centre to the
 * finger. One record fixes both: the drag applies a snapped DELTA to the value
 * the element already had, so a zero-movement gesture is exactly a no-op and an
 * authored 137 mm start stays 137 mm plus whatever you moved it.
 */
export interface PlanDragOrigin {
	/** Pointer position along the element's wall at pointerdown, mm. */
	alongMm: number;
	/** The element's stored scalar at pointerdown (start / offset / xMm), mm. */
	valueMm: number;
}

/** The element's own stored scalar, for the axis a drag moves it along. */
export function planDragOriginValueMm(
	target: PlanDragTarget,
	layout: InteriorLayout,
	openings: OpeningLike[],
): number {
	switch (target.kind) {
		case "bench": {
			const run = layout.benchRuns.find((entry) => entry.id === target.runId);
			return run?.startMm ?? 0;
		}
		case "bench-edge": {
			const run = layout.benchRuns.find((entry) => entry.id === target.runId);
			if (!run) return 0;
			return target.edge === "start" ? run.startMm : run.startMm + run.lengthMm;
		}
		case "heater":
			return layout.heater.offsetMm;
		case "door":
			return openings.find((entry) => entry.id === target.openingId)?.xMm ?? 0;
	}
}

/** The wall a target is measured along, for converting a pointer to `alongMm`. */
export function planDragWall(
	target: PlanDragTarget,
	layout: InteriorLayout,
	openings: OpeningLike[],
): InteriorWall {
	if (target.kind === "heater") return layout.heater.wall;
	if (target.kind === "door") {
		const opening = openings.find((entry) => entry.id === target.openingId);
		return (opening?.wall as InteriorWall) ?? "front";
	}
	const run = layout.benchRuns.find((entry) => entry.id === target.runId);
	return run?.wall ?? "back";
}

/**
 * Pointer position along a target's wall, in the same frame as its stored
 * scalar. Bench runs and the heater are measured from the finished ROOM's low
 * corner; an opening's `xMm` is measured from the OUTER wall line, so the two
 * need different origins.
 */
export function pointAlongTargetMm(
	target: PlanDragTarget,
	pointMm: PlanPointMm,
	layout: InteriorLayout,
	openings: OpeningLike[],
	context: PlanDragContext,
	wallOverride?: InteriorWall,
): number {
	const wall = wallOverride ?? planDragWall(target, layout, openings);
	if (target.kind === "door") {
		const halfMm = wallRunsAlongX(wall)
			? (context.dimensions.width / 2) * 1000
			: (context.dimensions.length / 2) * 1000;
		return (wallRunsAlongX(wall) ? pointMm.xMm : pointMm.zMm) + halfMm;
	}
	const room =
		target.kind === "heater"
			? context.room
			: roomForRun(
					layout.benchRuns.find((entry) => entry.id === target.runId)?.room ??
						"sauna",
					context,
				);
	return pointAlongWallMm(pointMm, room, wall);
}

/**
 * Which wall a gesture is currently over, or `undefined` when the target cannot
 * change wall.
 *
 * Only the aggregat may leave the wall it started on; a run stays on the one it was
 * authored against. That is a RULE, not plumbing, so it lives here next to
 * `resolvePlanDrag` rather than once per editing surface — if benches ever gain
 * wall-switching, one copy would have kept the old answer.
 */
export function wallHintForTarget(
	target: PlanDragTarget,
	pointMm: PlanPointMm,
	layout: InteriorLayout,
	room: InteriorRoom,
): InteriorWall | undefined {
	return target.kind === "heater"
		? nearestWallForPoint(pointMm, room, layout.heater.wall)
		: undefined;
}

/**
 * The wall a point sits nearest, for dragging the heater from one to another.
 *
 * `hysteresisMm` keeps the current wall unless the pointer is clearly nearer
 * another one: without it a heater dragged along a wall flickers between walls
 * near a corner, where two distances are almost equal.
 */
export function nearestWallForPoint(
	pointMm: PlanPointMm,
	room: InteriorRoom,
	currentWall: InteriorWall,
	hysteresisMm = 250,
): InteriorWall {
	const distances: Record<InteriorWall, number> = {
		left: Math.abs(pointMm.xMm - room.minXMm),
		right: Math.abs(room.maxXMm - pointMm.xMm),
		back: Math.abs(pointMm.zMm - room.minZMm),
		front: Math.abs(room.maxZMm - pointMm.zMm),
	};
	let best: InteriorWall = currentWall;
	for (const wall of ["left", "right", "back", "front"] as InteriorWall[]) {
		if (distances[wall] < distances[best]) best = wall;
	}
	if (best === currentWall) return currentWall;
	// Only leave the current wall on a decisive margin.
	return distances[currentWall] - distances[best] > hysteresisMm
		? best
		: currentWall;
}

/**
 * Identity of one blocking issue, for comparing a candidate against the layout
 * the gesture started from.
 *
 * Three things have to be in here:
 *
 *  - the ROOM. `free-floor` names neither a run nor a room, so without it every
 *    free-floor error in the design collapsed onto one identity and a violation
 *    in the relax room excused a new one in the hot room.
 *  - the run(s). A bench-overlap issue names one run in `runId` and the other
 *    in `args.otherRunId`, and which is which depends on array order, so an
 *    identity has to carry both or "did dragging B onto A introduce this?" is
 *    unanswerable. Order itself is stable across a gesture — a drag never
 *    reorders `benchRuns` — so the pair is used as reported.
 *  - the door, for a swing issue, which is not symmetric with the run.
 */
function issueIdentity(roomId: string, issue: InteriorLayoutIssue): string {
	const other = issue.args.otherRunId ?? issue.args.doorId ?? "";
	return `${roomId}|${issue.code}|${issue.runId ?? ""}|${other}`;
}

/**
 * The measured value an issue reports, where the rule wants it LARGER —
 * remaining free floor, the gap to the heater, the depth of a seat. Null for
 * the issues that are purely binary (a swing overlaps a bench, or it does not).
 *
 * This is what makes "may not make it worse" mean something: an identity alone
 * cannot tell a repair from a deepening of the same violation.
 */
function issueMargin(issue: InteriorLayoutIssue): number | null {
	const actual = issue.args.actualSqm ?? issue.args.actualMm;
	return typeof actual === "number" ? actual : null;
}

/**
 * How bad each blocking issue was allowed to be before the gesture.
 * Identities absent from this map are violations the drag introduced.
 *
 * One entry per identity is enough: `validateInteriorLayout` raises at most one
 * error per (room, code, run, door) — one free-floor verdict per room, one
 * clearance error per dropped run, one swing error per door/run pair — so there
 * is nothing to merge.
 */
function blockingBaseline(result: PlanValidationResult): Map<string, number> {
	const map = new Map<string, number>();
	for (const { roomId, issue } of result.scoped) {
		if (issue.severity !== "error") continue;
		map.set(
			issueIdentity(roomId, issue),
			issueMargin(issue) ?? Number.NEGATIVE_INFINITY,
		);
	}
	return map;
}

/** Floating-point slack; every value compared here is already rounded. */
const MARGIN_EPSILON = 1e-6;

/**
 * Did this gesture introduce (or deepen) the issue?
 *
 * An unknown identity is new. A known one is tolerated only while the measured
 * value has not gone backwards — which is what lets a user drag a bench out of
 * a room that is already too full without the drag being refused for the
 * violation it is in the middle of repairing.
 */
function isIntroduced(
	baseline: Map<string, number>,
	{ roomId, issue }: RoomScopedIssue,
): boolean {
	const key = issueIdentity(roomId, issue);
	const before = baseline.get(key);
	if (before === undefined) return true;
	const margin = issueMargin(issue);
	if (margin === null || before === Number.NEGATIVE_INFINITY) return false;
	return margin < before - MARGIN_EPSILON;
}

/**
 * Build the candidate layout for a bench or heater drag and judge it.
 *
 * `origin` anchors the gesture (see `PlanDragOrigin`); the pointer contributes
 * a movement, never an absolute position.
 */
export function resolvePlanDrag(input: {
	target: PlanDragTarget;
	layout: InteriorLayout;
	openings: OpeningLike[];
	pointMm: PlanPointMm;
	origin: PlanDragOrigin;
	context: PlanDragContext;
	/** Wall the pointer is over, for a heater dragged to a different wall. */
	wallHint?: InteriorWall;
	/**
	 * Movement supplied directly instead of derived from the pointer — the
	 * keyboard path. Routing arrow keys through this function rather than
	 * letting them write the store is what gives them reconciliation and
	 * validation; the only thing they need to bypass is the pointer arithmetic.
	 */
	alongDeltaMm?: number;
}): PlanDragResolution {
	const { target, layout, openings, pointMm, origin, context } = input;

	/** Snapped movement along the element's own axis. */
	const deltaFor = (wallOverride?: InteriorWall): number => {
		if (input.alongDeltaMm !== undefined) return input.alongDeltaMm;
		const alongMm = pointAlongTargetMm(
			target,
			pointMm,
			layout,
			openings,
			context,
			wallOverride,
		);
		return snapMm(alongMm - origin.alongMm);
	};

	// The layout the gesture started from, so a pre-existing violation elsewhere
	// can be told apart from one this drag just caused.
	if (target.kind === "door") {
		return resolveDoorDrag({ ...input, baseline: validateAllRooms(layout, context) });
	}

	let candidate = layout;

	if (target.kind === "bench" || target.kind === "bench-edge") {
		const run = layout.benchRuns.find((entry) => entry.id === target.runId);
		if (!run)
			return finish(layout, openings, context, null, undefined, validateAllRooms(layout, context));
		const nextMm = origin.valueMm + deltaFor();

		candidate =
			target.kind === "bench"
				? moveBenchRunAlongWall(layout, run.id, nextMm)
				: resizeBenchRun(layout, run.id, target.edge, nextMm);
	} else {
		const wall = input.wallHint ?? layout.heater.wall;
		if (wall === layout.heater.wall) {
			candidate = moveHeaterAlongWall(layout, origin.valueMm + deltaFor());
		} else {
			// Moving to a DIFFERENT wall has no shared axis to take a delta on —
			// the pointer's position on the new wall is the only meaningful
			// answer, so this is the one case that positions absolutely.
			const alongMm = pointAlongTargetMm(
				target,
				pointMm,
				layout,
				openings,
				context,
				wall,
			);
			candidate = moveHeaterToWall(layout, wall, snapMm(alongMm));
		}
	}

	// Reconciliation is the ONLY bounds authority: it trims a run that has been
	// pushed past a corner and pulls the heater back to the nearest position the
	// unit actually fits in. Doing that here with a local clamp is how the two
	// editing surfaces would drift.
	const focusId = target.kind === "heater" ? undefined : target.runId;
	return resolveInteriorEdit({ layout, candidate, openings, context, focusRunId: focusId });
}

/** Forms and drags share bounds, both-room validation and the repair baseline. */
export function resolveInteriorEdit(input: {
	layout: InteriorLayout;
	candidate: InteriorLayout;
	openings: OpeningLike[];
	context: PlanDragContext;
	focusRunId?: string;
}): PlanDragResolution {
	const { layout, candidate, openings, context, focusRunId } = input;
	const rooms = context.relaxRoom ? [context.room, context.relaxRoom] : [context.room];
	const reconciled = reconcileInteriorLayout(candidate, rooms, context.heaterSize);
	return finish(reconciled, openings, context, null, focusRunId, validateAllRooms(layout, context));
}

function finish(
	layout: InteriorLayout,
	openings: OpeningLike[],
	context: PlanDragContext,
	openingIssueKey: PlanOpeningIssueKey | null,
	focusRunId: string | undefined,
	baseline: PlanValidationResult,
): PlanDragResolution {
	const validation = validateAllRooms(layout, context);
	const capacity = calculateSeatingCapacity(
		{ layout, room: context.room, heaterSize: context.heaterSize },
		context.overrides ?? {},
	);

	// A drag is refused for what IT breaks, not for what was already broken.
	// Gating on `validation.valid` froze every drag as soon as the design held
	// any violation anywhere — so the plan view stopped working exactly when the
	// user needed it to repair something. "You may not make it worse, you may
	// make it better" is the rule — per room, and per size, or "worse" is not
	// something the comparison can see.
	const before = blockingBaseline(baseline);
	const introduced = validation.scoped
		.filter(
			(entry) =>
				entry.issue.severity === "error" && isIntroduced(before, entry),
		)
		.map((entry) => entry.issue);

	// The live explanation names what this gesture caused. Falling back to the
	// dragged element's own issues keeps advisory warnings visible.
	const focused =
		introduced.length > 0
			? introduced
			: focusRunId
				? validation.issues.filter((entry) => entry.runId === focusRunId)
				: validation.issues.filter((entry) => !entry.runId);

	return {
		layout,
		openings,
		valid: introduced.length === 0 && openingIssueKey === null,
		issues: focused,
		openingIssueKey,
		capacity,
	};
}

/**
 * Doors move through `setOpenings`, and their legality is
 * `validateOpeningSet`'s — the same domain gate the sliders, the 3D drag and
 * autosave all go through. `WallOpening.xMm` is measured from the low corner of
 * the OUTER wall line, so the pointer is converted against the building's own
 * half-extent rather than the finished room's.
 */
function resolveDoorDrag(input: {
	target: PlanDragTarget;
	layout: InteriorLayout;
	openings: OpeningLike[];
	pointMm: PlanPointMm;
	origin: PlanDragOrigin;
	context: PlanDragContext;
	baseline: PlanValidationResult;
	alongDeltaMm?: number;
}): PlanDragResolution {
	const { openings, pointMm, origin, context, layout, baseline } = input;
	const target = input.target as { kind: "door"; openingId: string };
	const opening = openings.find((entry) => entry.id === target.openingId);
	if (!opening) {
		return finish(layout, openings, context, null, undefined, baseline);
	}

	const wall = (opening.wall as InteriorWall) ?? "front";
	const runsAlongX = wallRunsAlongX(wall);
	const halfMm = runsAlongX
		? (context.dimensions.width / 2) * 1000
		: (context.dimensions.length / 2) * 1000;
	const deltaMm =
		input.alongDeltaMm ??
		snapMm(
			pointAlongTargetMm(target, pointMm, layout, openings, context) -
				origin.alongMm,
		);
	const xMm = origin.valueMm + deltaMm;

	const nextOpenings = openings.map((entry) =>
		entry.id === opening.id ? { ...entry, xMm } : entry,
	);

	const result = validateOpeningSet({
		openings: nextOpenings,
		dimensions: context.dimensions,
		// The hot room carries the design's wall height (issue 155).
		wallHeightMm: context.room.wallHeightMm,
    hasRelaxRoom: context.relaxRoom !== null,
    partitionPositionMm: context.relaxRoom ? ((context.room.minZMm < context.relaxRoom.minZMm ? context.room.maxZMm + context.relaxRoom.minZMm : context.relaxRoom.maxZMm + context.room.minZMm)/2 + context.dimensions.length*500) : undefined,
		overrides: context.overrides ?? {},
	});
	const mine = result.issues.filter((entry) => entry.openingId === opening.id);
	const openingIssueKey: PlanOpeningIssueKey | null =
		mine.length === 0
			? null
			: mine.some((entry) => entry.code === "overlap")
				? "plan_view_issue_openingOverlap"
				: "plan_view_issue_openingBounds";

	// A door that moved is also a door swing the benches must clear, so the
	// layout is re-validated against the moved door rather than the stored one.
	//
	// In the room it BELONGS to. `deriveInteriorDoors` is called once per room
	// and assigns each door to the room its span falls in, so a two-room's
	// exterior entry — which opens into the RELAX side in the stock layout —
	// exists only in `relaxDoors`. Rewriting `context.doors` alone rewrote
	// nothing, and `finish` then validated the relax benches against the door's
	// old sweep: a drag that put the real leaf straight through the relax lav
	// was reported as legal and committed.
	//
	// `startMm` is measured from its OWN room's low corner along the wall, so
	// each collection is re-anchored against its own rectangle. A door on a long
	// wall may legitimately span the divider and appear in both.
	const moveDoorsIn = (doors: InteriorDoor[], room: InteriorRoom) =>
		doors.map((door) =>
			door.id === opening.id
				? {
						...door,
						startMm: xMm - halfMm - wallAxisOriginMm(room, door.wall),
					}
				: door,
		);

	const movedContext: PlanDragContext = {
		...context,
		doors: moveDoorsIn(context.doors, context.room),
		relaxDoors: context.relaxRoom
			? moveDoorsIn(context.relaxDoors, context.relaxRoom)
			: context.relaxDoors,
	};

	const resolution = finish(
		layout,
		nextOpenings,
		movedContext,
		openingIssueKey,
		undefined,
		baseline,
	);
	return { ...resolution, openings: nextOpenings };
}
